export type EmissionControlTagColorScheme =
  | 'gray'
  | 'success'
  | 'error'
  | 'warning'
  | 'yellow'
  | 'sky'
  | 'blue';

interface StatusTag {
  label: string;
  colorScheme: EmissionControlTagColorScheme;
}

/**
 * Rótulos dos status do controle de emissão, em um lugar só — a listagem e o
 * detalhe mantinham mapas idênticos, que já começavam a divergir.
 *
 * Os rótulos são CURTOS de propósito: na listagem eles vivem numa coluna de
 * largura fixa, dentro de um container sem scroll horizontal, então um texto
 * longo empurra as colunas seguintes (inclusive a de Ações) para fora da tabela.
 * O texto completo aparece no filtro de status
 * ({@link EMISSION_CONTROL_STATUS_OPTIONS}) e na descrição da linha do histórico
 * de integração, onde há espaço.
 */
export const EMISSION_CONTROL_STATUS_CONFIG: Record<string, StatusTag> = {
  pending: { label: 'Pendente', colorScheme: 'yellow' },
  awaiting_manual_emission: {
    label: 'Emissão manual',
    colorScheme: 'warning',
  },
  processing: { label: 'Processando', colorScheme: 'sky' },
  reprocessing: { label: 'Reprocessando', colorScheme: 'sky' },
  done: { label: 'Concluído', colorScheme: 'success' },
  error: { label: 'Erro', colorScheme: 'error' },
  retry_limit_exceeded: { label: 'Limite de Tentativas', colorScheme: 'error' },
};

/** Status de uma LINHA do histórico de integração (não do controle). */
export const INTEGRATION_HISTORY_STATUS_CONFIG: Record<string, StatusTag> = {
  SUCCESS: { label: 'Sucesso', colorScheme: 'success' },
  ERROR: { label: 'Erro', colorScheme: 'error' },
};

/**
 * Mapa usado no DETALHE, onde os mesmos rótulos servem ao status do controle e
 * ao das linhas do histórico.
 */
export const EMISSION_CONTROL_AND_HISTORY_STATUS_CONFIG: Record<
  string,
  StatusTag
> = {
  ...EMISSION_CONTROL_STATUS_CONFIG,
  ...INTEGRATION_HISTORY_STATUS_CONFIG,
};

/**
 * Opções do filtro de status. Aqui o texto é COMPLETO: o combobox não tem a
 * restrição de largura da coluna, e é onde o operador procura o estado pelo nome.
 */
export const EMISSION_CONTROL_STATUS_OPTIONS = [
  { label: 'Todos', value: '' },
  { label: 'Pendente', value: 'pending' },
  { label: 'Aguardando emissão manual', value: 'awaiting_manual_emission' },
  { label: 'Processando', value: 'processing' },
  { label: 'Reprocessando', value: 'reprocessing' },
  { label: 'Concluído', value: 'done' },
  { label: 'Erro', value: 'error' },
  { label: 'Limite de Tentativas', value: 'retry_limit_exceeded' },
];

export const EMISSION_CONTROL_STEP_LABELS: Record<string, string> = {
  initial: 'Inicial',
  insurer_i4pro: 'Segurado I4Pro',
  policy_i4pro: 'Apólice I4Pro',
  policy_query_i4pro: 'Consulta I4Pro',
  policy_cancellation_i4pro: 'Cancelamento',
  policy_insuremo: 'Apólice InsureMO',
  policy_pdf: 'PDF Apólice',
  policy_pdf_bucket: 'PDF Bucket',
  email_customer: 'E-mail Cliente',
  ecm_i4pro: 'Envio ECM I4Pro',
  payment_charge: 'Cobrança (link de pagamento)',
  internal_email: 'E-mail Interno',
  finished: 'Finalizado',
  // RD Equipamentos (workflow próprio, sem Policy no InsureMO) — ver
  // Portal.Api/docs/rd-equipamentos-i4pro-emissao.md, Design §2.
  rd_proposal_i4pro: 'Proposta I4Pro',
  rd_items_i4pro: 'Itens I4Pro',
  rd_premium_i4pro: 'Cálculo de Prêmio',
  rd_proposal_status_i4pro: 'Status da Proposta',
  rd_policy_i4pro: 'Apólice I4Pro (RD)',
};

export const INTEGRATION_FLOW_LABELS: Record<string, string> = {
  SYSTEM: 'Sistema',
  USER: 'Usuário',
};

export const INTEGRATION_PROVIDER_LABELS: Record<string, string> = {
  I4PRO: 'I4Pro',
  INSUREMO: 'InsureMO',
  AWS: 'AWS',
  FINANCE: 'Finance.API',
  // RD Equipamentos: PDF e confirmação de emissão vêm do Motor de Cálculo,
  // nunca do InsureMO (RD não tem Doc Generation lá).
  CALC_ENGINE: 'Motor de Cálculo',
};
